/**
 * ControlPanel API のリクエスト/レスポンス型定義。
 *
 * docs/openapi/control-panel-api.yaml の `BroadcastPoint` スキーマに対応する。
 *
 * Issue (本 PR): 1 つの放送地点に複数のコンテンツ (`contents[]`) を持たせ、
 * カテゴリ別のプロパティ (自列車遅延しきい値 / 接続線 route_id / 任意 SSML 等) を
 * 設定できるようにした。`delay_threshold_seconds` は「自列車遅延」コンテンツの
 * プロパティへ移動したため、トップレベルの同名フィールドは廃止 (互換目的で
 * 受け取りはするが、フォーム上では編集しない)。
 */
/** コンテンツのカテゴリ識別子 */
export type BroadcastContentCategory =
  | 'self_train_delay'
  | 'connecting_line_delay'
  | 'connecting_line_delay_by_station'
  | 'train_info'
  | 'free_text';

/**
 * 放送粒度。遅延分数を何分単位で切り捨てて放送するかを表す。
 * - `1分単位`: 1分単位で切り捨て
 * - `5分単位`: 5分単位で切り捨て
 * - `10分単位`: 10分単位で切り捨て
 * - `大幅な遅延`: 分数を読み上げず「大幅な遅れ」として放送
 */
export type DelayBroadcastGranularity = '1分単位' | '5分単位' | '10分単位' | '大幅な遅延';

/** 遅延放送パターン (遅れ分数の幅ごとに1行) */
export interface DelayBroadcastPattern {
  ssml_template_i18n?: Record<string, string>;
  /** 遅れ開始分数 (この分数以上の遅れから対象) */
  delay_minutes_from: number;
  /**
   * 遅れ終了分数 (この分数未満まで対象)。
   * 未設定の場合は上限なし (最後の行として機能する)。
   */
  delay_minutes_to?: number;
  /** 放送粒度 */
  granularity: DelayBroadcastGranularity;
  /**
   * SSML テンプレート (任意)。
   * 省略時は `granularity` に対応したデフォルト文言を使用する。
   */
  ssml_template?: string;
}

/** 自列車遅延コンテンツのプロパティ */
export interface SelfTrainDelayProperties {
  /**
   * 遅延放送パターン一覧。
   * 遅れ分数の幅ごとに放送粒度と SSML テンプレートを設定する。
   */
  delay_patterns: DelayBroadcastPattern[];
}

/**
 * 接続線遅延の判定モード。
 * - `whole_line` (条件1): 接続線全体の最大遅延のみで判定する純化されたモード。
 *   区間 (start/end_stop_id) / 乗換駅 (transfer_stop_id) / 方面 (direction_id) は
 *   判定にも放送文 (路線名のみ読み上げ) にも一切使用しない。
 *   「区間で絞りたい」場合は `section_scheduled`、
 *   「乗換列車を考慮したい」場合は `transfer_aware` を使う。
 * - `section_scheduled` (条件2): 区間 + 定刻在線便の遅延で判定 (StopTimes 定刻在線経路)。
 * - `transfer_aware` (条件3): 乗換時間を考慮し「まさに乗り換えようとしている便」の遅延で判定。
 * - `departure_forecast` (条件4): 乗換駅から接続便を特定し、遅延分数ではなく
 *   **発車見込み時刻** を案内する。接続便の特定は `transfer_aware` と共通
 *   (乗換駅・乗換時間・方面が必須)。遅れ分数の範囲 (`delay_patterns`) は
 *   「案内する遅れ分数の範囲」として使い、粒度は参照しない。
 *
 * **放送条件 (任意テキスト放送) には `departure_forecast` を出さない。**
 * 放送条件の判定モードは {@link ConnectingConditionJudgementMode} (3 種) を使うこと。
 */
export type ConnectingDelayJudgementMode =
  | 'whole_line'
  | 'section_scheduled'
  | 'transfer_aware'
  | 'departure_forecast';

/**
 * 放送条件 ({@link ConnectingLineDelayCondition}) で選べる判定モード。
 *
 * 接続線遅延コンテンツの {@link ConnectingDelayJudgementMode} から
 * `departure_forecast` を除いた 3 種。発車見込み案内は「遅延分数の大小」を比較する
 * 放送条件とは意味が合わないため、条件側には出さない (型で保証する)。
 */
export type ConnectingConditionJudgementMode = Exclude<
  ConnectingDelayJudgementMode,
  'departure_forecast'
>;

/**
 * 接続線遅延コンテンツ (part1 / part2 共通) に紐付ける「運行情報の参照元」1 件。
 *
 * 接続線遅延の判定対象は ATOS の `route_id` だが、運行情報の参照元として
 * **ATOS の線名コードと一致しないアイステイションズ路線** を複数選択できる。
 * ATOS ↔ アイステイションズの相互変換は行わず、選択された路線コードを
 * そのまま保持する (バックエンドでも変換・整合性チェックはしない)。
 *
 * 紐付けは静的マスタではなく運用設定 (コンテンツ設定) 側に保持するため、
 * 対応関係の追加・変更にデプロイは不要となる。
 *
 * 選択肢のマスタは `web-app/src/data/istationsLines.ts` (`istations-lines.json`)。
 */
export interface IStationsLineRef {
  /** アイステイションズ側の路線コード (例: `"3101"`)。ATOS の route_id とは別系統。 */
  route_id: string;
  /**
   * 選択時点のアイステイションズ路線名称 (任意 / 表示用スナップショット)。
   * マスタ側の名称が変わっても保存時点の表示名を画面に出せるようにするための
   * 冗長保持であり、判定には使用しない。
   */
  route_name?: string;
}

/** 接続線遅延コンテンツのプロパティ */
export interface ConnectingLineDelayProperties {
  ssml_template_i18n?: Record<string, string>;
  /** 監視対象の接続線 route_id */
  route_id: string;
  /**
   * この接続線の運行情報の参照元となるアイステイションズ路線 (任意 / 複数選択)。
   *
   * part1 (`connecting_line_delay`) は part2 のような `lines[]` 配列を持たない
   * フラット構造のため、`properties` 直下に保持する
   * (part2 は {@link ConnectingLineEntry.istations_lines} 側)。
   *
   * 未指定 / 空配列なら従来動作 (アイステイションズ路線の紐付けなし)。
   * 上限は 10 件 (サーバ側 `MAX_ISTATIONS_LINES` と一致させること)。
   */
  istations_lines?: IStationsLineRef[];

  /**
   * 接続線の線区愛称 (任意)。設定時は放送 SSML の `{route_name}` / `{route}` プレースホルダに
   * **最優先** で使用する。
   *
   * 例) 接続線 `route_id: "東貨"` の放送で「湘南新宿ラインが遅延しています」と読ませたい場合、
   *     このフィールドに `"湘南新宿ライン"` を設定する。
   *
   * 空文字 / 未指定なら `route_name` (Transformer routeName) → `f"{route_id}線"` にフォールバック。
   */
  route_nickname?: string;
  /**
   * この接続線の線区愛称の **言語別名称** (任意) — Issue #579 (SBI-09)。
   *
   * キーは language_key (`ja` / `en` / `zh` / `ko`)、値はその言語での線区名。
   *
   * SSML 線名解決の優先順位:
   *   1. `route_nickname_i18n[<放送言語>]`
   *   2. `route_nickname_i18n.ja`   (当該言語が未登録の場合)
   *   3. `route_nickname`           (従来の単一文字列 = ja 相当)
   *   4. `route_name` → `f"{route_id}線"`
   *
   * 例) 英語放送で「the Shonan-Shinjuku Line: for Shinjuku, about 5 minutes late」と
   *     読ませたい場合、`{ en: 'Shonan-Shinjuku Line' }` を設定する。
   *
   * **後方互換**: 本フィールドが無い既存データは `route_nickname` (単一文字列) が
   * `ja` として解決されるため、放送文は変わらない。
   */
  route_nickname_i18n?: Record<string, string>;
  /**
   * 接続線遅延の閾値秒数。
   *
   * - `whole_line` / `section_scheduled` モードでは「最大遅延がこの秒数以上なら放送」の単一閾値として使用する。
   * - `transfer_aware` モードでは `delay_patterns` (遅れ分数の幅ごとに粒度・テンプレートを設定) を
   *   優先するため、本フィールドは「未設定 (delay_patterns が無い旧データ) 時のフォールバック閾値」
   *   としてのみ使用される。
   */
  delay_threshold_seconds: number;
  /**
   * 遅延判定モード。
   * 未指定の既存データはバックエンド側で入力項目から自動推定される (後方互換)。
   */
  delay_judgement_mode?: ConnectingDelayJudgementMode;
  /**
   * `transfer_aware` モード専用の「遅れ分数の範囲ごと放送パターン」設定。
   *
   * - 自列車遅延 (`SelfTrainDelayProperties.delay_patterns`) と同じデータ形状で、
   *   遅れ分数の幅ごとに放送粒度 (1分/5分/10分単位 / 大幅な遅延) と任意 SSML テンプレートを持つ。
   * - 指定された場合、`transfer_aware` モードでは `delay_threshold_seconds` ではなく
   *   各行の `delay_minutes_from` ～ `delay_minutes_to` 範囲で放送可否を判定し、
   *   該当行の粒度に応じて切り捨てた分数 (`{delay_minutes_1/5/10}`) を放送文に展開する。
   * - 未指定の場合は従来通り `delay_threshold_seconds` + 単一 `ssml_template` で判定する (後方互換)。
   * - `whole_line` / `section_scheduled` モードでは本フィールドは無視される。
   */
  delay_patterns?: DelayBroadcastPattern[];

  /**
   * 乗換時間 (秒)。`delay_judgement_mode === 'transfer_aware'` のときに使用する。
   * 自列車が乗換駅に到着する予想時刻にこの秒数を足した時刻以降に発車する接続便を
   * 「まさに乗り換えようとしている列車」とみなす。
   */
  transfer_time_seconds?: number;

  /**
   * 遅延判定開始駅 (stop_id)。任意。
   * `delay_judgement_mode === 'section_scheduled'` のときに区間の起点として使う。
   * `whole_line` モードでは判定にも放送文にも使用しない (バックエンド側で無視)。
   */
  start_stop_id?: string;
  /**
   * 遅延判定終了駅 (stop_id)。任意。
   * `delay_judgement_mode === 'section_scheduled'` のときに区間の終点として使う。
   * `whole_line` モードでは判定にも放送文にも使用しない (バックエンド側で無視)。
   */
  end_stop_id?: string;
  /**
   * 乗換駅 (stop_id)。任意。
   * `transfer_aware` では発車時刻取得元 + 放送文の読み上げ駅。
   * `section_scheduled` では放送文の読み上げ駅 (`{from_station}`) として使い、
   * 未設定なら `start_stop_id` を読み上げ駅としてフォールバックする。
   * `whole_line` モードでは判定にも放送文にも使用しない (バックエンド側で無視)。
   */
  transfer_stop_id?: string;
  /** SSML テンプレート (任意) */
  ssml_template?: string;

  /**
   * 方面。接続線 (route_id) の方向マスタ (routes-master.json の `directions`) における
   * 並び順 (index) を文字列化した値。
   * 例: `directions[0]` を選んだ場合は `"0"`、`directions[1]` なら `"1"`。
   * 未指定 (空文字 / undefined) の場合は方面を限定しない。
   * `whole_line` モードでは判定にも放送文にも使用しない (バックエンド側で無視)。
   *
   * ※ この `direction_id` は **判定用** であり、`/route-stops` / `/stop-times` API の
   *   クエリパラメータとして渡されるダイヤ判定対象方向のインデックスである。
   *   放送文 (`{direction_name}` プレースホルダ) に差し込む方面文言は別フィールド
   *   `direction_name` (任意の自由入力テキスト) を使う。
   */
  direction_id?: string;

  /**
   * 案内文面 (SSML) の `{direction_name}` プレースホルダに差し込む方面文言 (任意入力)。
   *
   * - 判定用の `direction_id` とは独立した、放送文専用の自由入力テキスト。
   * - 空文字 / 未指定の場合はプレースホルダに空文字が展開される (= 方面を読み上げない)。
   * - `whole_line` モードでは判定にも放送文にも使用しない (バックエンド側で無視)。
   *
   * 例: `direction_id="1"` (千葉方面（B線）便で判定) + `direction_name="千葉"`
   *     → 放送文 (乗換駅=錦糸町): 「錦糸町駅からお乗換の中央総武緩行線千葉方面行きは、現在1分遅れています」
   *     ※ `whole_line` モードでは「乗換の中央総武緩行線は、現在1分遅れています」のように
   *       駅名・方面を読み上げない純化された文面になる。
   */
  direction_name?: string;
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の方面1つぶんの設定。
 *
 * part1 (`ConnectingLineDelayProperties`) と違い、
 * 「乗換駅を主語にした案内」の中で 1 接続線につき 1〜2 方面を並べる用途で使う。
 *
 * - `direction_id`: 判定用の方向インデックス (routes-master.json の `directions` の並び順)。
 *   `whole_line` モードでは使用しない (バックエンド側で無視)。
 * - `direction_name`: 案内文面用の自由入力テキスト (例: `"新宿"` → 放送文中では「新宿方面は…」)。
 *   part1 と違い、放送文の主要トークンとなるため **必須入力** として扱う。
 * - `start_stop_id` / `end_stop_id`: `section_scheduled` モードで区間を絞るときの起点・終点。
 *   part2 では方面ごとに区間を持たせられる (方面によって走行区間が異なるケースに対応)。
 */
export interface ConnectingDirectionEntry {
  /**
   * 判定用の方向インデックス ("0" / "1")。`whole_line` モードでは無視。
   *
   * **画面では編集しない** (線区ごとの {@link route_directions} に一本化した)。
   * 既存データ互換のため放送側のフォールバックとしては残り、
   * 画面は線区ごとの表の初期値として表示する。表を編集すると `route_directions` へ
   * 展開されて本フィールドは取り除かれる。
   */
  direction_id?: string;
  /**
   * **線区ごとの判定用方面** (任意)。キーが `route_id`、値が `direction_id`。
   *
   * ## 何のための項目か
   *
   * {@link ConnectingLineEntry.route_ids} で遅延検索対象の線区を複数指定したとき、
   * **線区によって「同じ向き」を表す `direction_id` が異なる** ことがある。
   * 例) 大宮から見て「宮原・上尾方面」に相当する向きは
   *
   *   | route_id                       | direction_id | 方面名 |
   *   | ------------------------------ | ------------ | ------ |
   *   | 高崎線                          | `"0"`        | 下り   |
   *   | 上野東京ライン-高崎系統          | `"1"`        | 北行   |
   *   | 湘南新宿ライン-高崎東海道系統    | `"1"`        | 北行   |
   *
   * のように揃わない。方面ブロック共通の {@link direction_id} だけでは
   * 全線区に同一の値が使われてしまい、この組み合わせを表現できないため、
   * **線区単位の上書き** を本フィールドで保持する。
   *
   * ## 解決順 (放送側 `_resolve_route_direction_id`)
   *
   * 1. `route_directions[route_id]` に非空値があればそれを使う
   * 2. 無ければ方面ブロック共通の {@link direction_id} を使う (旧データ互換。画面からは設定しない)
   *
   * つまり **未設定なら既存データと 1 バイトも変わらない** (後方互換)。
   *
   * ## 制約
   *
   * - 値は `"0"` / `"1"` のみ (サーバ側 `_normalize_direction_overrides` が 400 を返す)。
   * - キーは `route_ids` に含まれる route_id を想定するが、`routes-master.json` との
   *   整合はサーバ側で強制しない (`route_ids` と同じ方針)。
   * - 空オブジェクトはキーごと省略して保存する (既存 item と bit-perfect 互換)。
   * - `whole_line` モードでは方面を判定に使わないため無視される。
   */
  route_directions?: Record<string, string>;
  /** 案内文面用の方面名 (例: "新宿")。放送文の `{direction_name}` に展開される。 */
  direction_name: string;
  /**
   * 方面名の **言語別名称** (任意)。キーは language_key (`en` / `zh` / `ko` 等)。
   *
   * 英中韓の放送で `{direction_name}` に展開する名称 (例: `{ en: "Hachioji" }`)。
   * 未設定の言語は日本語の {@link direction_name} で放送される (放送を止めない) が、
   * 放送言語に含まれる言語が未入力だと保存時に弾かれる (日本語の混入を防ぐため)。
   * 日本語は {@link direction_name} が担うため、ここには入れない。
   */
  direction_name_i18n?: Record<string, string>;
  /** 遅延判定開始駅 (stop_id)。`section_scheduled` モードで使用。 */
  start_stop_id?: string;
  /** 遅延判定終了駅 (stop_id)。`section_scheduled` モードで使用。 */
  end_stop_id?: string;
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の接続線1本ぶんの設定。
 *
 * 1つの `ConnectingLineDelayByStationProperties` 配下に複数並べられる。
 * それぞれの接続線ブロックは判定モード / 乗換時間 / 放送パターンを独立に持ち、
 * 内部で 1〜2 の `ConnectingDirectionEntry` (方面) を持つ。
 */
export interface ConnectingLineEntry {
  ssml_line_template_i18n?: Record<string, string>;
  /**
   * 接続線 route_id (例: `"埼京"`)。
   *
   * 放送文の `{route_id}` / `route_name` 解決 / ログに使う **メイン値**。
   * 遅延の検索対象にも **常に含まれる** (検索対象 = メイン + {@link ConnectingLineEntry.route_ids})。
   */
  route_id: string;
  /**
   * メイン {@link ConnectingLineEntry.route_id} に **加えて遅延を検索する線区 (route_id) の一覧** (任意 / 複数指定)。
   *
   * 検索対象は **メイン + この一覧** (メインは常に含まれる)。放送側 (UpdateContents) は
   * 1 つの案内文を生成する。`transfer_aware` / `departure_forecast` は発車見込みが
   * **最も早い接続便**、`whole_line` / `section_scheduled` は全対象の **最大遅延** を採る。
   * 例) 大宮の高崎線下りは「東北下り」と「東北貨物下り」の遅れの影響を受けるため
   *     メイン `高崎線` + `route_ids: ["東北", "東北貨"]` を設定する。
   *
   * - **未指定 / 空配列ならメイン {@link ConnectingLineEntry.route_id} のみを検索**する
   *   (後方互換。既存データの放送文は 1 バイトも変わらない)。
   * - メインと同じ値が含まれていても検索は 1 回。検索順は メイン → この一覧の順。
   * - メイン route_id (`{route_id}` プレースホルダ / `route_name` 解決 / アイステ文面) は
   *   常に `route_id` であり、この一覧の先頭ではない。
   * - **重複不可** (サーバ側 `_normalize_connecting_route_ids` が 400 を返す)。
   * - 上限は 10 件 (サーバ側 `MAX_CONNECTING_ROUTE_IDS` と一致させること)。
   * - `routes-master.json` との整合は強制されない (ATOS 線名コード / 系統 route_id の
   *   どちらでも指定できる)。
   *
   * UI では `route_id` とあわせて「遅延を検索する線区」の 1 つの一覧 + 1 つのプルダウン
   * (`ConnectingRouteIdsSelect`) で入力する (画面では区別せず、先頭 = `route_id`、2 件目以降 = この配列)。全解除すると
   * `undefined` を emit してキーごと保存しない (既存 item と bit-perfect 互換)。
   */
  route_ids?: string[];
  /**
   * この接続線の線区愛称 (任意)。設定時は放送 SSML の `{route_name}` / `{route}` プレースホルダに
   * **最優先** で使用する。
   *
   * 例) 接続線ブロック内で「湘南新宿ライン、新宿方面は5分遅れ…」と読ませたい場合、
   *     このフィールドに `"湘南新宿ライン"` を設定する。
   *
   * 空文字 / 未指定なら `route_name` (Transformer routeName) → `f"{route_id}線"` にフォールバック。
   */
  route_nickname?: string;
  /**
   * この接続線の線区愛称の **言語別名称** (任意) — Issue #579 (SBI-09)。
   *
   * キーは language_key (`ja` / `en` / `zh` / `ko`)、値はその言語での線区名。
   *
   * SSML 線名解決の優先順位:
   *   1. `route_nickname_i18n[<放送言語>]`
   *   2. `route_nickname_i18n.ja`   (当該言語が未登録の場合)
   *   3. `route_nickname`           (従来の単一文字列 = ja 相当)
   *   4. `route_name` → `f"{route_id}線"`
   *
   * 例) 英語放送で「the Shonan-Shinjuku Line: for Shinjuku, about 5 minutes late」と
   *     読ませたい場合、`{ en: 'Shonan-Shinjuku Line' }` を設定する。
   *
   * **後方互換**: 本フィールドが無い既存データは `route_nickname` (単一文字列) が
   * `ja` として解決されるため、放送文は変わらない。
   */
  route_nickname_i18n?: Record<string, string>;
  /**
   * この接続線の運行情報の参照元となるアイステイションズ路線 (任意 / 複数選択)。
   *
   * ATOS の `route_id` と一致しない路線も自由に選べる (変換は行わない)。
   * 詳細は {@link IStationsLineRef} を参照。
   *
   * 未指定 / 空配列なら従来動作 (アイステイションズ路線の紐付けなし)。
   * 上限は 10 件 (サーバ側 `MAX_ISTATIONS_LINES` と一致させること)。
   */
  istations_lines?: IStationsLineRef[];
  /** 判定モード (part1 と同じ 3 種) */
  delay_judgement_mode: ConnectingDelayJudgementMode;

  /** 乗換時間 (秒)。`transfer_aware` モードで使用。 */
  transfer_time_seconds?: number;
  /**
   * 接続線ブロックの SSML テンプレート (任意)。
   * 未指定時はデフォルト文言 (`{route_name}、{directions_text}となっております。`) を使用。
   *
   * 使えるプレースホルダ:
   * - `{route_name}` / `{route_id}` / `{route}` (route_name のエイリアス)
   * - `{directions_text}` (方面部分文を読点で連結した文字列)
   */
  ssml_line_template?: string;
  /**
   * この接続線ブロックの遅延放送パターン。
   * 遅れ分数の幅ごとに放送粒度 (1分単位/5分単位/10分単位/大幅な遅延) と
   * SSML テンプレートを設定する。方面ごとには持たず、2 方面で共通ルール。
   *
   * 各パターンの `ssml_template` は **方面部分文** (`{directions_text}` を組み立てる素材) を表す。
   * 例: `"{direction_name}方面は{delay_minutes_5}分遅れ"`
   */
  delay_patterns: DelayBroadcastPattern[];
  /** この接続線で案内する方面 (1〜2 件)。順序は放送順に対応。 */
  directions: ConnectingDirectionEntry[];
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の「乗換駅の指定方法」モード。
 *
 * - `station` (既定): 従来どおり自路線の駅一覧から乗換駅を 1 つ選ぶ。
 *   遅延判定 (3 モード) + 運行情報の併記が行える通常モード。
 * - `other`: 乗換駅を指定しない「その他」モード。遅延判定は一切行わず、
 *   接続線ごとに選択した **アイステイションズ路線の運行情報 (TrainInfoText)** のみを
 *   放送する。
 *
 * ⚠️ **`other` は UI からは設定できない (レガシー)**。運行情報のみを放送したい場合は
 * 独立カテゴリ {@link TrainInfoProperties} (`train_info` / 「運行情報」) を使う。
 * 本値は既に保存済みのデータ (放送側 `_is_transfer_stop_other_mode`) を
 * 壊さず放送し続けるための **読み取り互換** としてのみ残している。
 *
 * **後方互換**: 本フィールドを持たない既存データは `station` として扱うため、
 * 既存 BroadcastSpots レコードのマイグレーションは不要。
 * また `station` は既定値と同義なので、サーバ側では保存しない (キーごと省略) ことで
 * 既存 item と bit-perfect 互換を維持する。
 */
export type ConnectingStationTransferStopMode = 'station' | 'other';

/**
 * 接続線遅延 (乗換駅ベース / part2) で、放送する接続線数が上限を超えたときの動作。
 *
 * - `replace_with_phrase`: 全ての遅延案内文を、固定の案内文 1 本に差し替える。
 * - `truncate`: 上限本数まで放送し、以降は放送しない。
 * - `truncate_then_replace` (併用): 遅延のある接続線が上限を超えたら先頭から上限本数まで放送し、
 *   さらに `replace_over_lines` を超えたら固定の案内文 1 本に差し替える。
 *
 * 値を変えるときは、サーバ側 `ALLOWED_OVER_LIMIT_BEHAVIORS`
 * (`lambda/control-panel-api/index.py`) と OpenAPI の enum も同時に更新すること。
 */
export type ConnectingStationOverLimitBehavior =
  | 'replace_with_phrase'
  | 'truncate'
  | 'truncate_then_replace';

/**
 * 接続線遅延 (乗換駅ベース / part2) のコンテンツプロパティ。
 *
 * 1 コンテンツ = 1 乗換駅で、その乗換駅から乗り換えられる複数の接続線を並べる。
 * part1 (`ConnectingLineDelayProperties`) との併存を前提とし、判定処理は独立している
 * (Lambda 側で `CONNECTING_LINE_DELAY_BY_STATION_CATEGORY` 分岐として実装)。
 *
 * 放送イメージ:
 * ```
 * 「赤羽駅からの                                     ← ssml_intro_template
 *   埼京線、新宿方面は5分遅れ、大宮方面は3分遅れとなっております。 ← lines[0]
 *   東北本線、上野方面は7分遅れとなっております。」                ← lines[1]
 * ```
 *
 * `transfer_stop_mode === 'other'` (その他モード) の場合は乗換駅・遅延判定を使わず、
 * 各 `lines[]` に紐付けたアイステイションズ路線の運行情報のみを放送する。
 */
export interface ConnectingLineDelayByStationProperties {
  ssml_intro_template_i18n?: Record<string, string>;
  /**
   * 乗換駅の指定方法 (任意)。未設定は `station` (従来動作) と同義。
   * `other` (その他) のときは乗換駅を使わず、運行情報のみを放送する。
   * 詳細は {@link ConnectingStationTransferStopMode} を参照。
   */
  transfer_stop_mode?: ConnectingStationTransferStopMode;
  /**
   * 乗換駅 (stop_id)。`transfer_stop_mode` が `station` (既定) のときは必須。
   * 親フォームで選択した路線の駅リストから選ぶ。
   * `other` (その他) モードでは使用しないため空文字となる。
   */
  transfer_stop_id: string;
  /**
   * 乗換駅名の **言語別名称** (任意)。キーは language_key (`en` / `zh` / `ko` 等)。
   *
   * 英中韓の放送で `{transfer_station}` に展開する駅名 (例: `{ en: "Higashi-Kanagawa" }`)。
   * 未設定の言語は日本語の駅名 ({@link transfer_stop_id}) で放送される (放送を止めない) が、
   * 放送言語に含まれる言語が未入力だと保存時に弾かれる (日本語の混入を防ぐため)。
   * 日本語は {@link transfer_stop_id} が担うため、ここには入れない。
   */
  transfer_stop_name_i18n?: Record<string, string>;

  /**
   * 冒頭 SSML テンプレート (任意)。
   * 未指定時はデフォルト文言 (`{transfer_station}駅からお乗換の`) を使用。
   *
   * 使えるプレースホルダ:
   * - `{transfer_station}` (乗換駅の駅名)
   */
  ssml_intro_template?: string;
  /**
   * 放送する接続線数の上限 (任意)。正の整数。未設定は「制限しない」。
   *
   * 数えるのは、遅延案内文が空でない接続線の件数。運行情報のみの接続線は数えない。
   * `lines` の件数以上の値を設定しても超過は起きない (制限なしと同じ動作)。
   */
  max_broadcast_lines?: number;
  /**
   * 上限を超えたときの動作。`max_broadcast_lines` を設定するときは必須。
   * 未設定のまま `max_broadcast_lines` だけを送ると、サーバが 400 を返す。
   */
  over_limit_behavior?: ConnectingStationOverLimitBehavior;
  /**
   * 固定文に切り替える上限 (任意)。正の整数。`over_limit_behavior` が `truncate_then_replace`
   * のときだけ使い、そのときは必須。`max_broadcast_lines` より大きい値に限る
   * (`max_broadcast_lines` が無いときは比べない)。それ以外の動作で送るとサーバが 400 を返す。
   *
   * 遅延のある接続線がこの本数を **超えた** ときに固定文へ切り替える。
   */
  replace_over_lines?: number;
  /**
   * 上限超過時の案内文 (任意)。`over_limit_behavior` が `replace_with_phrase` /
   * `truncate_then_replace` のときに使う。
   * 未指定時は定型文マスタ `connecting_station_over_limit` の当該言語の文面を使う。
   *
   * 使えるプレースホルダ:
   * - `{transfer_station}` (乗換駅の駅名)
   */
  over_limit_ssml_template?: string;
  /** 上限超過時の案内文の言語別文面。キーは language_key。 */
  over_limit_ssml_template_i18n?: Record<string, string>;
  /**
   * 接続線一覧 (1 件以上、上限なし)。順序は放送順に対応。
   * それぞれの接続線ブロックは独立した判定モードと放送パターンを持つ。
   */
  lines: ConnectingLineEntry[];
}

/**
 * 運行情報 (`train_info`) のコンテンツプロパティ。
 *
 * 遅延判定を一切行わず、接続線ごとに選択した **アイステイションズ路線の運行情報
 * (TrainInfoText) の原文のみ** を放送する。乗換駅・冒頭 SSML・判定モード・方面・
 * 遅延パターン・各 SSML テンプレートは持たない。配信が無ければ放送しない。
 *
 * 保存するのは `lines[].istations_lines` だけ (ControlPanelApi が他のキーを捨てる)。
 * 各 line に `istations_lines` が 1 件以上必要 (放送内容が空になるため)。
 * 旧「その他」モード (`connecting_line_delay_by_station` + `transfer_stop_mode: 'other'`)
 * を独立カテゴリへ昇格したもの。
 */
export interface TrainInfoProperties {
  /** 運行情報の参照元の組。順序が放送順に対応する (1 件以上)。 */
  lines: TrainInfoLineEntry[];
}

/** 運行情報 (`train_info`) の 1 ブロック。 */
export interface TrainInfoLineEntry {
  /** 運行情報の参照元となるアイステイションズ路線 (1 件以上 / 複数選択)。 */
  istations_lines?: IStationsLineRef[];
}

/**
 * 任意テキスト放送コンテンツの入力モード。
 * - `text`: 通常のテキスト入力 (API 側で `<speak>` ラップして合成)
 * - `ssml`: SSML を直接入力
 * - `audio`: 音声ファイル (MP3) をアップロード
 */
export type FreeTextInputMode = 'text' | 'ssml' | 'audio' | 'synced';

/**
 * 任意テキスト放送コンテンツの放送条件で使用する比較演算子。
 *
 * - `gte`: 「delay_minutes 以上」であれば真
 * - `lte`: 「delay_minutes 以下」であれば真
 *
 * (シンプル運用のため 2 種類のみをサポートする)
 */
export type BroadcastConditionOperator = 'gte' | 'lte';

/**
 * 放送条件の種別 (判別可能ユニオンの discriminant)。
 *
 * - `connecting_line_delay`: 接続線の遅延で判定する (従来からある条件種別)。
 * - `arrival_track`: 自列車の停車番線 (GTFS 子 stop_id) で判定する
 *   (「特定番線停車時のみ放送」用)。識別子は歴史的経緯により `arrival_track` のまま
 *   据え置いている (Athena / 保存済み JSON との互換のため)。
 *
 * **後方互換**: 既存データ (`condition_type` を持たない条件) は
 * `connecting_line_delay` として扱うため、既存 BroadcastSpots レコードの
 * マイグレーションは不要。
 */
export type BroadcastConditionType = 'connecting_line_delay' | 'arrival_track';

/**
 * 接続線遅延を条件とする放送条件 1 件 (従来の放送条件)。
 *
 * 接続線遅延 (`ConnectingLineDelayProperties`) の判定モードごとの入力項目を踏襲し、
 * 「(route_id, 判定モード, モード別入力, 遅延分数, 演算子)」で 1 条件を表現する。
 *
 * - `delay_minutes` は分単位。バックエンドでは秒に換算して既存の遅延判定ロジックと比較する。
 * - 複数条件を並べた場合は AND (すべて満たす場合のみ放送) と解釈される。
 * - `FreeTextProperties.conditions` が未指定 / 空配列なら「無条件放送」(後方互換)。
 *
 * `route_id` / `delay_minutes` / `operator` は **必須のまま維持** する。
 * 停車番線条件はこれらを持たないため、optional 化やダミー値ではなく
 * 別インタフェース ({@link ArrivalTrackCondition}) で表現する。
 */
export interface ConnectingLineDelayCondition {
  /**
   * 条件種別 (判別可能ユニオンの discriminant)。
   *
   * **省略可**。未指定は本種別 (`connecting_line_delay`) として扱うため、
   * `condition_type` を持たない既存データもそのまま解釈できる。
   */
  condition_type?: 'connecting_line_delay';
  /** 監視対象の接続線 route_id (必須) */
  route_id: string;
  /**
   * 遅延判定モード。接続線遅延コンテンツの判定モードのうち、
   * 発車見込み案内 (`departure_forecast`) を除く 3 種類。
   * 未指定の場合は `whole_line` として扱う。
   */
  delay_judgement_mode?: ConnectingConditionJudgementMode;
  /**
   * 比較する遅延分数 (0 以上の整数)。バックエンドで秒に換算して評価する。
   */
  delay_minutes: number;
  /** 比較演算子 (以上 / 以下) */
  operator: BroadcastConditionOperator;

  // --- section_scheduled モード用 (方面ごとに区間を絞る) ---
  /** 遅延判定開始駅 (stop_id) */
  start_stop_id?: string;
  /** 遅延判定終了駅 (stop_id) */
  end_stop_id?: string;

  // --- transfer_aware モード用 (乗換駅・乗換時間) ---
  /** 乗換駅 (stop_id) */
  transfer_stop_id?: string;
  /** 乗換時間 (秒) */
  transfer_time_seconds?: number;

  /**
   * 方面 (判定用・任意)。routes-master.json の direction_id または配列 index。
   * `section_scheduled` / `transfer_aware` で StopTimes / route-stops の direction_id
   * クエリとして使う。`whole_line` では未使用。
   */
  direction_id?: string;
}

/**
 * 停車番線を条件とする放送条件 1 件 (「特定番線停車時のみ放送」用)。
 *
 * 「駅名」＋「番線名」の 2 項目ではなく、**GTFS 子 stop_id 1 項目** で保持する。
 * 子 stop_id は駅と番線を一意に表す文字列 (例: `"南浦和:上中"`) であり、
 * 発車標 (OBA-TrainDepartureBoardWebApp) が利用している GtfsStatic API
 * `GET /gtfs/stops` の値をそのまま使う。
 *
 * この設計により以下が不要になる:
 * - 駅と番線の整合バリデーション (駅未選択で番線のみ設定 等)
 * - 番線名を文字列として保つための数値化 (Decimal 混入) 対策
 *   (`"南浦和:上中"` のような文字列は数値解釈されない)
 *
 * 判定ロジック自体は本 SBI のスコープ外 (後続 SBI で実装)。
 */
export interface ArrivalTrackCondition {
  /**
   * 条件種別 (判別可能ユニオンの discriminant)。
   *
   * 停車番線条件では **必須**。未指定は `connecting_line_delay` として
   * 解釈されるため、本種別では明示的に指定しなければならない。
   */
  condition_type: 'arrival_track';
  /**
   * 停車番線 (GTFS 子 stop_id / 必須)。例: `"南浦和:上中"`。
   * 選択肢は GtfsStatic API `GET /gtfs/stops` の子 stop_id から取得する。
   *
   * 判定は「trip の `stop_times` 全行の子 stop_id 集合にこの値が含まれるか」で、
   * 終着駅・途中停車駅のどちらでも成立する (通過駅は行が無いため不成立)。
   */
  arrival_stop_id: string;
}

/**
 * 任意テキスト放送 (free_text) の放送条件 1 件。
 *
 * `condition_type` を discriminant とする判別可能ユニオン。
 * 未指定 (= 既存データ) は {@link ConnectingLineDelayCondition} として扱う。
 */
export type BroadcastCondition = ConnectingLineDelayCondition | ArrivalTrackCondition;

/** 任意テキスト放送コンテンツのプロパティ */
export interface FreeTextProperties {
  /**
   * 放送文面の通常テキスト。
   * `input_mode` が `text` のときに使用し、API 側で SSML にラップして合成する。
   */
  text?: string;
  /**
   * 放送文面の SSML (`<speak>...</speak>`)。
   * `audio_key` が指定されている場合は省略可能 (アップロード音声を優先する)。
   */
  ssml?: string;
  /**
   * アップロード済み音声ファイル (原本) の S3 キー (例: `uploads/<uuid>.mp3`)。
   * 指定された場合は Polly 合成をスキップし、このファイルを放送音声に使用する。
   *
   * Issue #415: 肉声音源の原本 (マスタ) は `POST /audio-uploads` が `uploads/` 配下に
   * 保存する。`audio/` 配下は「いつでも削除してよい配信用の複製置き場」であり、
   * UpdateContents が放送コンテンツ生成時に `audio/upload-<uuid>.mp3` へ
   * (まだコピーされていなければ) コピーして端末へ配信する。
   * 端末は `GET /audio/{audio_key}` を「`audio/` 固定」前提で解決するため、
   * 原本キーをそのまま配信すると 404 になる。
   */
  audio_key?: string;
  /**
   * GUI 上で選択中の入力モード。
   * 省略時は既存データから推測する (`audio_key` → audio / `ssml` → ssml / それ以外 → text)。
   */
  input_mode?: FreeTextInputMode;
  /**
   * 共有テキスト (`input_mode === 'synced'`) の参照先。
   *
   * 本文は放送地点に保存せず、共有テキストマスタ (`SyncedTextMaster`) の行を参照する。
   * マスタの編集は放送地点を編集しなくても約 1 分以内に放送へ反映される。
   * 日本語専用 (参照できるのは放送言語が `['ja']` の場合のみ)。
   */
  synced_ref?: SyncedTextRef;
  /**
   * 放送条件 (任意・複数可)。
   *
   * - 未指定 or 空配列: 従来通り無条件で放送する (後方互換)。
   * - 複数指定: すべて満たす場合のみ放送する (AND 判定)。
   *
   * 各条件は接続線遅延コンテンツの判定モードを踏襲する。詳細は {@link BroadcastCondition} を参照。
   */
  conditions?: BroadcastCondition[];
  /**
   * 言語別の放送文面 (通常テキスト) — Issue #449。
   *
   * キーは `BroadcastPoint.languages` と同じ language_key (`en` / `zh` / `ko` …)、
   * 値はその言語の文面。受け入れ条件「入力テキストの任意放送は、日本語から
   * 他言語へ翻訳した文字を設定できること」に対応する。
   *
   * - `text` (サフィックス無し) が **日本語** の文面を表す。`ja` を本辞書に
   *   入れても無視される (日本語は常に `text` を使う = 後方互換)。
   * - 当該言語の文面が未登録なら、放送側は `text` (日本語) へフォールバックする
   *   (無音にせず、翻訳の入れ忘れに気付けるようにする方針)。
   * - 空文字は「未設定」と同義で保存されない。
   */
  text_i18n?: Record<string, string>;
  /** 言語別の放送文面 (SSML)。規約は {@link FreeTextProperties.text_i18n} と同じ。 */
  ssml_i18n?: Record<string, string>;
  /**
   * 言語別のアップロード音源キー。規約は {@link FreeTextProperties.text_i18n} と同じ。
   *
   * 肉声音源を言語ごとに差し替えたい場合に使う。未指定の言語は `audio_key`
   * (日本語向けにアップロードした音源) がそのまま使われる。
   */
  audio_key_i18n?: Record<string, string>;
}

export type BroadcastContentProperties =
  | SelfTrainDelayProperties
  | ConnectingLineDelayProperties
  | ConnectingLineDelayByStationProperties
  | TrainInfoProperties
  | FreeTextProperties
  | Record<string, unknown>;

/**
 * 曜日識別子 (放送スケジュールの `days_of_week` で使用)。
 *
 * ISO 8601 の曜日番号ではなく、可読性を優先して英語 3 文字の略称で表現する。
 * バックエンド Lambda (`_ALLOWED_DAYS`) と OpenAPI enum (`BroadcastSchedule.days_of_week`)
 * とキーが一致していること。
 */
export type DayOfWeek = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

/**
 * 放送時間帯 1 レンジ。`start_time` / `end_time` は `HH:mm` 形式 (24 時間表記)。
 *
 * `start_time > end_time` (例: `"22:00" - "05:00"`) は **日跨ぎ** を意味し、
 * 端末側判定でその日の 22:00〜翌日 05:00 の連続時間帯として扱う。両者が同一値の場合は
 * 「その瞬間 1 秒のみ」ではなく「無効な範囲」として扱う (Lambda 側で 400 相当)。
 */
export interface TimeRange {
  /** 開始時刻 (`HH:mm`)。例: `"07:00"` */
  start_time: string;
  /** 終了時刻 (`HH:mm`)。例: `"10:00"`。start > end で日跨ぎ許容。 */
  end_time: string;
}

/**
 * コンテンツ単位の放送スケジュール条件。
 *
 * - 判定はサーバ側では行わず、端末が **ローカル時刻でリアルタイム判定** する
 *   (詳細は `docs/broadcast-schedule.md` を参照)。
 * - サーバは本設定を DynamoDB に格納し、`GET /contents/{trip_id}` レスポンスの
 *   `contents_schedule[].schedule` を通してそのまま端末に配信する。
 * - すべてのフィールドが未指定 (`undefined` / 空配列) の場合、当該コンテンツは
 *   **常時放送** として扱う (schedule 自体を保存しないのと等価)。
 * - 個別フィールドの意味 (端末側判定ロジック):
 *   - `days_of_week`: 指定曜日のみ放送。未指定 / 空配列は「全曜日で放送」。
 *   - `time_ranges`: 指定時間帯 (複数可) のいずれかに現在時刻が含まれるときのみ放送。
 *     未指定 / 空配列は「24 時間放送」。`start > end` の要素は日跨ぎ扱い。
 *   - `start_date` / `end_date`: 有効期間 (両端含む)。片方のみでも OK。両者未指定は「期限なし」。
 */
export interface BroadcastSchedule {
  /** 放送を許可する曜日一覧。未指定 / 空配列で全曜日許可。 */
  days_of_week?: DayOfWeek[];
  /** 放送を許可する時間帯一覧。未指定 / 空配列で 24 時間放送 (日跨ぎは start > end で表現)。 */
  time_ranges?: TimeRange[];
  /** 放送開始日 (`YYYY-MM-DD`)。未指定なら過去は無制限。 */
  start_date?: string;
  /** 放送終了日 (`YYYY-MM-DD`、両端含む)。未指定なら未来は無制限。 */
  end_date?: string;
}

export interface BroadcastContent {
  languages?: string[];
  /** カテゴリ */
  category: BroadcastContentCategory;
  /** カテゴリ固有のプロパティ */
  properties: BroadcastContentProperties;
  /**
   * 放送スケジュール条件 (任意)。
   *
   * 未指定 = 「常時放送」(従来動作と同一)。設定された場合、端末側が受信済みデータを
   * ローカル時刻で毎回評価して放送要否を判定する。サーバ側は判定に関与せず、値を
   * そのまま `GET /contents/{trip_id}` の `contents_schedule[].schedule` に載せて配信する。
   * 詳細は `docs/broadcast-schedule.md` を参照。
   */
  schedule?: BroadcastSchedule;
  /**
   * 再生速度倍率 (任意 / 0.5 以上 2.0 以下)。
   *
   * - サーバは音声合成には関与せず、値をそのまま
   *   `GET /contents/{trip_id}` の `contents_schedule[].playback_rate` に透過して
   *   端末側に配信する。
   * - 端末側は再生時にこの値を **`HTMLAudioElement.playbackRate`** (WebApp) や
   *   **`ExoPlayer.setPlaybackSpeed`** (Android) に反映して再生する。
   * - 未指定 / `1.0` は等倍再生 (後方互換)。管理画面の「再生速度」入力欄で
   *   `1.0` に戻した場合は本フィールドを削除して保存する (未指定と同義)。
   * - 範囲外 (0.5 未満 / 2.0 超) の値は ControlPanel API 側で 400 を返す。
   * - 複数コンテンツを合成音源としてまとめる経路では、単一コンテンツかつ
   *   `playback_rate` 指定ありの場合のみ Core がトップレベルに引き継ぐ。
   *   複数マージで混在時は 1.0 (未指定) に強制フォールバックする。
   */
  playback_rate?: number;
  /**
   * 列車種別 (任意)。`'all'` = 全列車 / `'rapid'` = 快速 / `'local'` = 各駅停車。
   *
   * - 未指定 / `'all'` は「全列車」を意味する (後方互換)。ControlPanel API は
   *   `'all'` を **未設定と同義** として扱い、DynamoDB item には
   *   `train_type` キーを保存しない (既存データと bit-perfect 互換)。
   * - `'rapid'` / `'local'` は **列車種別を持つ線区のみ** 設定できる。
   *   線区が持つ種別は `setting/routes-master.json` の `train_types`
   *   (`web-app/src/data/routes.ts` の `RouteMaster.train_types`) が正源で、
   *   整合しない指定は ControlPanel API 側で 400 を返す
   *   (UI 側だけの制御にはしない)。
   * - `properties` の外 (`playback_rate` と同じトップレベル) に置く。
   */
  train_type?: 'all' | 'rapid' | 'local';
  /**
   * 放送対象の列車番号 (列番) の配列 (任意 / Issue #530)。
   *
   * - 未指定 / 空配列は「全列車」を意味する (後方互換)。ControlPanel API は
   *   空配列に落ちた場合も **未設定と同義** として扱い、DynamoDB item には
   *   `train_numbers` キーを保存しない (既存データと bit-perfect 互換)。
   * - 複数指定でき、`trip_id` (`<運行日>:<路線>:<列番>:<方向>`) の列番セグメントが
   *   **いずれかに一致** する列車でのみ放送する (**OR 条件**)。
   * - 各要素は半角英数字とハイフンのみ (最大 16 文字 / 最大 100 件)。
   *   全角文字や空白入りの値は ControlPanel API 側で 400 を返す
   *   (`_coerce_train_numbers`)。UI 側だけの制御にはしない。
   * - 重複は 400 にせず、先に現れた順を保って除去される (比較は英字を大文字化)。
   * - **判定はサーバ側 (UpdateContents Lambda) で行い端末は非関与**。
   *   `train_type` と同時に指定した場合は **AND 条件** (両方満たす列車のみ)。
   * - `properties` の外 (`playback_rate` / `train_type` と同じトップレベル) に置く。
   */
  train_numbers?: string[];
  /**
   * コンテンツ単位の有効 / 無効 (任意 / Issue #810)。
   *
   * - `false` のときだけ保存する。`true` / 未指定は有効 (後方互換・既存 item と bit-perfect 互換)。
   * - 無効のコンテンツは UpdateContents が放送対象から外す。
   * - 放送地点の `enabled` (Issue #679) と連動する: 地点を切り替えると全コンテンツに反映され、
   *   全コンテンツが無効になると地点も無効になる。
   * - 真偽値以外は ControlPanel API が 400 を返す。`properties` の外 (トップレベル) に置く。
   */
  enabled?: boolean;
}

/**
 * 保存系 API (作成 / 更新 / 削除 / 一括系) のレスポンスが持つ、変更履歴の登録結果 (マスタ変更履歴 SBI 8)。
 *
 * 履歴の登録はベストエフォートで、失敗してもマスタ本体の保存は成功する (HTTP ステータスも変わらない)。
 * 旧バージョンの API はこの項目を返さないため、どちらも省略可能。
 *
 * ⚠️ これはレスポンス専用。エンティティの型 (`Chime` など) には混ぜない。
 *    混ぜると、画面が受け取った値を次の更新リクエストの body に載せ戻す事故につながる。
 */
export interface HistoryRecordResult {
  /** `false` のとき、履歴の登録に失敗している。未定義 (旧 API) は失敗扱いにしない */
  historyRecordSucceeded?: boolean;
  /** 一括系で、履歴の登録に失敗した件数 */
  historyFailedCount?: number;
}

export interface BroadcastPoint {
  /** 線区と方面の組み合わせ (例: "京浜#北行") */
  routeDirection: string;
  /**
   * 放送地点の一意 ID (UUID v7)。
   * ユーザーには表示しない / 入力させない (バックエンド管理項目)。
   */
  spotId: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  radius_meters?: number;
  /**
   * 放送遅延 (放送をトリガしてから実放送まで遅らせる秒数)。
   * 地点共通の値で、コンテンツ毎ではなく地点に紐づく。
   */
  broadcast_delay_seconds?: number;
  /**
   * 放送地点単位で切り替える Amazon Polly の VoiceId (Neural 対応話者)。
   *
   * - 選択肢は `web-app/src/data/pollyVoices.ts` の `POLLY_VOICE_OPTIONS` を参照。
   * - 未指定 / 空文字はシステムデフォルト (`"Takumi"`) にフォールバックする。
   * - 用途は **放送 SSML の Polly 合成 (声色) のみ** に限定される。
   *   遅延判定 (`route_id` ベース) やデータ取得 (GTFS-RT / stop-times) には影響しない。
   * - Polly 合成音声の S3 キーは `voice_id` に加えて `LanguageCode` / `Engine` も
   *   含めてハッシュ化されるため、同一 SSML でも合成パラメータが違えば S3 上で
   *   衝突せず別ファイルとして共存する
   *   (`lambda/update-contents/audio_store.py` の `compute_audio_hash` を参照)。
   * - 編集モーダルの音声プレビュー (POST /audio-preview) にも同値が渡されるため、
   *   保存前に実放送と同じ声色で試聴できる。
   *
   * **多言語対応 (Issue #449)**: 本フィールドは「日本語の話者」を表す旧スキーマとして
   * 維持される。言語ごとに話者を分けたい場合は `voice_settings` を使う。
   */
  voice_id?: string;
  /**
   * この放送地点で放送する言語の一覧 (language_key) — Issue #449。
   *
   * - 値は `web-app/src/data/pollyVoices.ts` の `POLLY_LANGUAGE_KEYS` のいずれか。
   *   基本 4 言語 (`ja` / `en` / `zh` / `ko`) に加え、Polly が対応するその他の言語も選べる。
   * - サーバ側で **マスタ定義順に正規化され、重複は排除される**。放送は
   *   「言語ごとに全コンテンツをまとめて流す」順序 (【コンテンツ①】【コンテンツ②】[日本語]
   *   → 【コンテンツ①】【コンテンツ②】[英語] …) で行うため、言語の順序が
   *   放送地点ごとの入力順で揺れてはいけない。フロント側でも保存前に
   *   `normalizeLanguageKeys()` で同じ順序に揃えておくと表示と実放送が一致する。
   * - **未指定 / 空配列は「日本語のみ」** (= 従来と同一の挙動)。既存の放送地点は
   *   本フィールドを持たないため、設定を変えない限り挙動が変わらない。
   * - 指定できる言語数の上限は `MAX_SPOT_LANGUAGES` (8)。
   */
  languages?: string[];
  /**
   * 言語ごとの音声設定 (language_key → 設定) — Issue #449。
   *
   * 例) `{ ja: { voice_id: 'Kazuha' }, en: { voice_id: 'Joanna' } }`
   *
   * - 指定のない言語 / `voice_id` 未指定の言語は、その言語の既定話者を使う
   *   (`defaultVoiceIdForLanguage()` 相当)。
   * - **その言語で使える話者でなければ 400**。「英語の放送地点に日本語話者 (Takumi) を
   *   設定する」ような誤りは実放送で `LanguageNotSupportedException` を招き
   *   **その言語だけ無音** になるため、保存前に `isVoiceIdValidForLanguage()` で
   *   チェックしておくとよい (サーバ側も同じ検証を行う)。
   * - 後方互換: `voice_settings` を送りつつ `ja` を省略した場合、トップレベル
   *   `voice_id` が `voice_settings.ja.voice_id` として読み替えられる。
   *   `voice_settings` 自体を送らない場合は属性が作られず、既存データの形も変わらない。
   */
  voice_settings?: Record<string, LanguageVoiceSetting>;
  /**
   * 放送 **開始** 時に鳴らすチャイム音 (要件 3・4)。
   *
   * `Chime.chime_id` (チャイムマスタの ID) を指定する。未指定 / 空文字は
   * 「開始チャイムを鳴らさない」を意味する (バックエンドは DynamoDB に保存しない)。
   *
   * UpdateContents は当該チャイムの音源を解決し、`GET /contents/{trip_id}`
   * レスポンスの **トップレベル** `start_chime` (`{chime_id, audio_key}`) として
   * 配信する (既存音源との合成は行わない)。端末は放送キューの **先頭** に付与する。
   *
   * 【旧仕様からの変更】以前は `contents_schedule[]` の先頭に `is_chime: true` の
   * エントリとして差し込んでいたが、端末のスケジュール判定で本体コンテンツが全て
   * 範囲外になった場合でもチャイムだけが鳴ってしまう (チャイムのみ 2 回放送) 問題と、
   * `content_index: minimum: 0` へのスキーマ違反があったため撤回した。
   *
   * チャイムは **本体放送が 1 件以上再生される場合のみ** 鳴らす仕様で、
   * 本体が 0 件なら鳴らしてはならない (抑止は端末側の責務)。
   * 詳細は docs/openapi/driver-app-api.yaml の `Content.start_chime` を参照。
   */
  start_chime_id?: string;
  /**
   * 放送 **終了** 時に鳴らすチャイム音 (要件 3・4)。
   *
   * 仕様は start_chime_id と同じで、配信先が `GET /contents/{trip_id}` の
   * トップレベル `end_chime` となり、端末側での付与位置が放送キューの **末尾**
   * になる点だけが異なる。
   */
  end_chime_id?: string;
  /**
   * 放送地点の有効 / 無効 (Issue #679)。
   *
   * - `false` の地点は放送対象から外れる (UpdateContents が ContentsV2 を生成しない)。
   * - **未指定 / `true` は有効**。バックエンドは `false` のときだけ属性を保存するため、
   *   既存の放送地点 (属性なし) は変更なしで有効のまま動作する。
   * - 一覧のトグルからは `POST /broadcast-points/bulk-enabled` で部分更新する
   *   (`PUT` は全置換のため、有効 / 無効だけを変える用途には使わない)。
   */
  enabled?: boolean;
  /**
   * コンテンツ一覧 (順序を保つ。フロントでは drag/move で並び替え可能)。
   */
  contents?: BroadcastContent[];
  /**
   * @deprecated トップレベルの遅延しきい値は廃止予定。
   * 既存データ (Issue #56 時代) を読み込めるように残しているが、
   * 新規/更新では自列車遅延コンテンツのプロパティ側に移行する。
   */
  delay_threshold_seconds?: number;
}

/**
 * 1 言語ぶんの音声設定 (`BroadcastPoint.voice_settings` の値) — Issue #449。
 *
 * `engine` は保持しない。話者から言語マスタ (`pollyVoices.ts`) が一意に決めるため、
 * 冗長に持つと「マスタは neural なのに設定は standard」という矛盾状態を作れてしまう。
 */
export interface LanguageVoiceSetting {
  /**
   * 当該言語で使用する Polly VoiceId。
   *
   * その言語が対応する話者でなければサーバ側で 400 になる
   * (`isVoiceIdValidForLanguage()` で保存前に検証できる)。
   * 未指定 / 空文字はその言語の既定話者にフォールバックする。
   */
  voice_id?: string;
}

/** POST /broadcast-points のリクエストボディ */
export type BroadcastPointCreateRequest = Omit<BroadcastPoint, 'spotId'> & {
  /** 省略時は API 側で UUID v7 を採番 */
  spotId?: string;
};

/** 有効 / 無効の切り替え対象 (routeDirection + spotId の複合キー) */
export interface BroadcastPointEnabledTarget {
  routeDirection: string;
  spotId: string;
}

/** POST /broadcast-points/bulk-enabled のリクエストボディ (Issue #679) */
export interface BroadcastPointBulkEnabledRequest {
  enabled: boolean;
  targets: BroadcastPointEnabledTarget[];
}

/** POST /broadcast-points/bulk-enabled のレスポンス (Issue #679) */
export interface BroadcastPointBulkEnabledResponse extends HistoryRecordResult {
  message: string;
  enabled: boolean;
  updated_count: number;
  updated: BroadcastPointEnabledTarget[];
  not_found: BroadcastPointEnabledTarget[];
  failed: BroadcastPointEnabledTarget[];
}

/** PUT /broadcast-points/{id} のリクエストボディ */
export type BroadcastPointUpdateRequest = Omit<BroadcastPoint, 'spotId'>;

/** POST / PUT /broadcast-points のレスポンス (放送地点 + 履歴の登録結果) */
export type BroadcastPointSaveResult = BroadcastPoint & HistoryRecordResult;

/** DELETE /broadcast-points/{id} のレスポンス */
export type BroadcastPointDeleteResult = HistoryRecordResult;

export interface BroadcastPointListResponse {
  items: BroadcastPoint[];
  count: number;
}

/**
 * 端末の動作ステータス。
 * - `normal`: 正常
 * - `abnormal`: 異常 (アラート送信済みの可能性あり)
 */
export type DeviceStatus = 'normal' | 'abnormal';

/**
 * 再起動要求の結果 (C-4)。
 * - `success`: AirDroid API が再起動要求を受理した
 * - `failed`: 再起動要求が失敗した (AirDroid API のエラー / 端末 ID 未解決など)
 *
 * サーバ側の正源は `lambda/reboot-scheduler/index.py` の
 * `RESULT_SUCCESS` / `RESULT_FAILED` (docs/nightly-reboot.md §8.4)。
 */
export type DeviceRebootResult = 'success' | 'failed';

/**
 * DeviceRegistry テーブルの 1 レコード
 * (端末ID⇄編成番号の最新紐づけ + 動作ステータス)。
 *
 * `GET /device-registrations` (ControlPanel API / SBI-5) が返す一覧の要素。
 * 各 `*_at` は ISO 8601 (JST) 文字列。未設定のフィールドは `null` を返す。
 */
export interface DeviceRegistration {
  /** 編成番号 (DeviceRegistry テーブルの PK) */
  vehicle_id: string;
  /** 端末 ID (GSI DeviceIdIndex のパーティションキー) */
  device_id: string;
  /**
   * 端末名 (任意入力 / Issue #360)。
   *
   * 端末死活管理画面で運用者が `PUT /device-registrations/{vehicle_id}` を通じて
   * 設定する表示名で、「どの車両のどの端末か」を人が識別しやすくするための項目。
   * 端末側の登録 API では書き込まれないため、未設定なら `null`。
   */
  device_name: string | null;
  /** 最終紐づけ登録時刻 (ISO 8601 / JST)。未設定なら null。 */
  last_registered_at: string | null;
  /** 動作ステータス。新規登録時は `normal` で初期化される。未設定なら null。 */
  status: DeviceStatus | null;
  /** ステータス更新時刻 (ISO 8601 / JST)。未設定なら null。 */
  status_updated_at: string | null;
  /** 最終アラート送信時刻 (ISO 8601 / JST)。未送信なら null。 */
  last_alert_sent_at: string | null;
  /**
   * 再起動を **要求** した時刻 (ISO 8601 / UTC) — C-4。
   *
   * 夜間定時再起動 (docs/nightly-reboot.md) とオンデマンド再起動
   * (`POST /device-registrations/{vehicle_id}/reboot`) のどちらでも、
   * RebootWorker が DeviceRegistry へ記録した値がそのまま返る。
   * 一度も再起動を要求していない端末では `null`。
   *
   * ⚠️ 「要求した時刻」であって「端末が再起動を完了した時刻」ではない
   * (AirDroid API へ要求を投げた時点で記録される)。
   */
  last_reboot_requested_at: string | null;
  /**
   * 再起動要求の結果 (C-4)。一度も要求していない端末では `null`。
   *
   * `success` は「AirDroid API が要求を受理した」ことを表し、端末が実際に
   * 再起動したことの保証ではない (dry-run 実行でも `success` になる)。
   */
  last_reboot_result: DeviceRebootResult | null;
  /**
   * AirDroid 側のタスク ID (C-4)。障害調査で AirDroid 管理コンソールと
   * 突き合わせるための識別子。
   *
   * AirDroid のレスポンスから取得できなかった場合は **空文字** が記録される。
   * 一度も再起動を要求していない端末では `null`。
   */
  last_reboot_task_id: string | null;
  /**
   * 現在の列番 (SBI-5)。
   *
   * DeviceRegistry ではなく OBA-Transformer 側 VehicleTripMapping テーブルの
   * SK (`trip_id`) から解決される合成値。以下の場合は `null` になる:
   *   - 当該編成が在線していない (直近 5 分以内に `updated_at` が更新されていない)
   *   - VehicleTripMapping の参照に失敗した (サーバ側フェイルセーフ)
   */
  current_trip_id: string | null;
  /**
   * AirDroid Business 上の端末 ID (C-4 / S-2)。
   *
   * 端末死活管理画面で運用者が `PUT /device-registrations/{vehicle_id}` を通じて
   * 設定する ID で、再起動が「どの編成のどの端末を再起動するか」を特定するために
   * 使う。AirDroid の管理コンソール上の端末 ID を転記する項目であり、
   * `device_id` (DriverApp が自動発行) とは体系が別物。
   *
   * DeviceRegistry ではなく DeviceIdMapping テーブル (PK=`device_id`) から解決される
   * **合成値**。`device_id` は AirDroid 側の端末 ID と一致する保証が無いため、
   * 再起動 API は必ずこのマッピングを引く。以下の場合は `null` になる:
   *   - 当該端末の AirDroid 端末 ID が DeviceIdMapping に未登録
   *   - DeviceIdMapping の参照に失敗した (サーバ側フェイルセーフ)
   *
   * `null` の端末は再起動 API が 409 を返すため、画面では「再起動できない端末」
   * として識別できる。
   */
  airdroid_device_id: string | null;
}

/** PUT /device-registrations/{vehicle_id} のレスポンス (端末 + 履歴の登録結果) */
export type DeviceRegistrationSaveResult = DeviceRegistration & HistoryRecordResult;

/** POST /device-registrations/bulk-delete のレスポンス (Issue #638) */
export interface DeviceRegistrationBulkDeleteResponse extends HistoryRecordResult {
  message: string;
  /** 削除したレコード数 */
  deleted_count: number;
  /** 削除できた編成番号 */
  deleted: string[];
  /** 既に存在しなかった編成番号 */
  not_found: string[];
  /** DynamoDB エラーで削除できなかった編成番号 */
  failed: string[];
}

/** GET /device-registrations のレスポンス */
export interface DeviceRegistrationListResponse {
  items: DeviceRegistration[];
  count: number;
  /** 次ページが存在する場合のカーソル (base64url)。最後のページでは未付与。 */
  nextToken?: string;
}

/**
 * `PUT /device-registrations/{vehicle_id}` のリクエストボディ (Issue #360 / C-1)。
 *
 * 画面は行単位の保存で **2 項目とも常に送る** (Issue #885)。
 *
 * ⚠️ サーバ側の PUT は編集対象 2 項目の全置換で、**キーが無い項目は解除される**。
 * 片方だけ送るともう片方が消えるため、変えない項目も現在値を送ること。
 * 値の解除は空文字 / `null` で表現する。
 */

export interface DeviceRegistrationUpdateRequest {
  /**
   * 端末名 (任意入力 / 最大 100 文字)。
   * 空文字 / 空白のみ / `null` は端末名の解除を意味する。
   */
  device_name?: string | null;
  /**
   * AirDroid 端末 ID (任意入力 / C-1 / S-2)。管理画面で運用者が転記する
   * 「AirDroid Business 上の端末 ID」で、再起動対象の特定に使われる。
   * DriverApp が自動発行する `device_id` とは体系が別物。
   *
   * 空文字 / 空白のみ / `null` は設定の解除を意味する。
   * 許可文字は英数字と `-` / `_` / `.` / `:` の 1〜128 文字
   * (サーバ側 `_AIRDROID_DEVICE_ID_PATTERN` と一致させること)。
   *
   * 保存先は DeviceRegistry ではなく **DeviceIdMapping (PK=`device_id`)** (S-2)。
   * そのため当該編成に `device_id` が登録されていない (端末が一度もアプリに
   * 接続していない) 場合は紐づけ先が決まらず **400** になる。
   */
  airdroid_device_id?: string | null;
}

/**
 * `POST /device-registrations/{vehicle_id}/reboot` の 200 応答 (C-3)。
 *
 * RebootWorker (夜間定時再起動と同じ Lambda) の戻り値をほぼそのまま透過する。
 *
 * ⚠️ `ok: true` は「RebootWorker まで要求が届き、Worker が成功を返した」ことを
 * 意味するだけで、**AirDroid が実際に端末を再起動したことは保証しない**
 * (`dry_run: true` のときは AirDroid API への HTTP を発行していない)。
 *
 * ⚠️ **仮実装 (C-5)**: AirDroid 再起動 API のリクエストフォーマットは疎通確認前
 * (docs/nightly-reboot.md §7 ①)。画面側も「要求の経路が成立した」ところまでしか
 * 表示できない。
 */
export interface DeviceRebootResponse extends HistoryRecordResult {
  /** 再起動を要求した編成番号 (リクエストのパスパラメータ)。 */
  vehicle_id: string;
  /**
   * DeviceRegistry に登録されている端末 ID (`web-` / `and-` プレフィックス付き)。
   * AirDroid 側の端末 ID とは別物。
   */
  device_id: string;
  /** DeviceIdMapping から解決した AirDroid 側の端末 ID (実際に要求を投げた対象)。 */
  airdroid_device_id: string;
  /**
   * RebootWorker が dry-run で処理したか。`true` の場合 AirDroid API への HTTP は
   * **発行されていない** (環境変数 `AIRDROID_DRY_RUN` / CDK props `airdroidDryRun`)。
   */
  dry_run: boolean;
  /** 再起動要求が成功したか。200 応答では常に `true` (失敗時は 502)。 */
  ok: boolean;
  /** AirDroid API の HTTP ステータス。dry-run 時 / HTTP 未到達時は null。 */
  http_status: number | null;
  /** AirDroid 側の再起動タスク ID。dry-run 時はダミー値、取得失敗時は null。 */
  task_id: string | null;
}

/**
 * 通知種別 (AlertRecipient.notify_types の要素)。
 *
 * - `alert`: 端末異常などのアラート通知 (alert-monitor が送信)
 * - `master_change`: 放送地点の変更通知 (対象線区で絞り込める)
 * - `devices`: 端末管理の「端末名 / デバイスID の登録・更新・クリア」「再起動」「選択した端末の削除」の通知 (Issue #806 / #885)
 * - `chimes` / `phrase_templates` / `ssml_dictionary` / `free_text_templates`:
 *   各設定の新規・編集・削除の通知 (Issue #806)。`free_text_templates` は共有テキストも含む
 *   (共有テキストを任意テキストに統合したため、旧 `synced_texts` は廃止。サーバが読み出し時に統合先へ読み替える)
 * - `user_admin` / `permission_group_admin` / `permission_assign`:
 *   ユーザー管理 / 権限グループ管理 / 権限付与の通知 (Issue #806)
 *
 * ⚠️ バックエンドの `_VALID_NOTIFY_TYPES` (`lambda/control-panel-api/index.py`) と
 *    **同じ文字列・同じ順序** に保つこと (順序はチェックボックスの表示順になる)。
 */
export type NotifyType =
  | 'alert'
  | 'master_change'
  | 'devices'
  | 'chimes'
  | 'phrase_templates'
  | 'ssml_dictionary'
  | 'free_text_templates'
  | 'user_admin'
  | 'permission_group_admin'
  | 'permission_assign';

/** 通知種別の一覧 (画面のチェックボックス表示順と同一)。 */
export const NOTIFY_TYPES: readonly NotifyType[] = [
  'alert',
  'master_change',
  'devices',
  'chimes',
  'phrase_templates',
  'ssml_dictionary',
  'free_text_templates',
  'user_admin',
  'permission_group_admin',
  'permission_assign',
];

/**
 * 通知種別の日本語表示名 (画面表示用)。
 *
 * ⚠️ メール末尾の注記 (`master_change_notify.NOTIFY_TYPE_LABELS`) と **同じ文言** に保つこと。
 *    メールの「このメールは「○○」に登録された宛先に送信されています」を見た人が、
 *    この画面で同じ名前を探せるようにするため。
 */
export const NOTIFY_TYPE_LABELS: Readonly<Record<NotifyType, string>> = {
  alert: 'アラート通知先',
  master_change: '放送地点の変更通知先',
  devices: '端末管理の通知先',
  chimes: 'チャイム音設定の変更通知先',
  phrase_templates: '定型文設定の変更通知先',
  ssml_dictionary: 'SSML辞書の変更通知先',
  free_text_templates: '任意テキスト (配布テンプレートを含む) の変更通知先',
  user_admin: 'ユーザー管理の変更通知先',
  permission_group_admin: '権限グループ管理の変更通知先',
  permission_assign: '権限付与の変更通知先',
};

/**
 * AlertRecipient テーブルの 1 レコード (所属区所単位の通知メール宛先) (SBI-6)。
 *
 * `GET /alert-recipients` が返す一覧の要素、および POST/DELETE レスポンスの
 * item フィールド。常に `depot_id`, `email`, `label`, `notify_types`, `route_ids`,
 * `updated_at` の 6 フィールドを持つ。
 */
export interface AlertRecipient {
  /** depot_id = 所属区所名 (例: "さいたま車両"; AlertRecipient テーブルの PK) */
  depot_id: string;
  /** 通知先メールアドレス (AlertRecipient テーブルの SK) */
  email: string;
  /** 表示名 (任意)。登録時に未指定の場合は null。 */
  label: string | null;
  /**
   * この宛先が受け取る通知の種別。常に 1 件以上。
   * DynamoDB に属性が無い既存レコードはバックエンドが `['alert']` に正規化して返す。
   */
  notify_types: NotifyType[];
  /**
   * 放送地点の変更通知を受け取る線区 (`routes-master.json` の `route_id`)。
   *
   * ⚠️ **空配列は「全線区」を意味する** (「1 つも受け取らない」ではない)。
   * バックエンドは属性を持たない既存レコードも空配列へ正規化して返すため、
   * 画面側で null / undefined を判別する必要はない。
   */
  route_ids: string[];
  /** 登録/更新時刻 (ISO 8601 / UTC)。 */
  updated_at: string;
}

/** POST /alert-recipients のリクエストボディ */
export interface AlertRecipientCreateRequest {
  /** depot_id = 所属区所名 (例: "さいたま車両"; 必須) */
  depot_id: string;
  /** 通知先メールアドレス (必須) */
  email: string;
  /**
   * 表示名 (任意)。
   * バックエンドは put_item で全フィールド置換するため、「未指定 = label をクリア」となる。
   * label を明示的にクリアする場合はこのフィールドを省略すること。
   */
  label?: string;
  /**
   * 通知種別 (任意)。1 件以上を指定する。
   * 省略時はバックエンドが後方互換のため `['alert']` として登録する。
   */
  notify_types?: NotifyType[];
  /**
   * 放送地点の変更通知を受け取る線区 (任意 / `routes-master.json` の `route_id`)。
   *
   * ⚠️ 省略・空配列はいずれも **「全線区」** として登録される。
   * バックエンドは put_item で全フィールド置換するため、絞り込みを解除して
   * 全線区に戻す場合は空配列を送る (または省略する)。
   */
  route_ids?: string[];
}

/** GET /alert-recipients のレスポンス */
export interface AlertRecipientListResponse {
  items: AlertRecipient[];
  count: number;
}

/** DELETE /alert-recipients のレスポンス */
export interface AlertRecipientDeleteResponse {
  message: string;
  item: AlertRecipient;
}

/**
 * ChimeMaster テーブルの 1 レコード (放送開始 / 終了チャイム音マスタ) (要件 3・4)。
 *
 * `GET /chimes` が返す一覧の要素、および POST / PUT / DELETE レスポンスの item。
 * 常に 6 フィールドを持ち、未設定の任意項目は `null` が返る。
 */
export interface Chime {
  /** チャイムの一意 ID (UUID v7 / ChimeMaster テーブルの PK) */
  chime_id: string;
  /** 管理画面での表示名 (例 "ポーン (開始)") */
  label: string | null;
  /**
   * S3 Audio バケット上の音源キー (`audio/chime-<uuid7>.mp3`)。
   *
   * チャイム音源は **必ず `audio/` プレフィックス配下** に保存される。
   * 端末は他の放送音源と同じ `GET /audio/{audio_key}` でチャイムを解決するが、
   * GetAudio Lambda はパスパラメータに `audio/` を機械的に前置するため、
   * `audio/` 配下でないと解決できない。旧 `chimes/` 配下の音源は端末で再生
   * できないため、音源を再アップロード (PUT /chimes/{chime_id}) して移行する。
   */
  audio_key: string | null;
  /** 音源長 (ミリ秒 / 任意メタ情報)。未設定なら null。 */
  duration_ms: number | null;
  /** 登録時刻 (RFC 3339 / UTC・`Z` 終端・秒精度) */
  created_at: string | null;
  /** 更新時刻 (RFC 3339 / UTC・`Z` 終端・秒精度) */
  updated_at: string | null;
}

/** POST /chimes のリクエストボディ */
export interface ChimeCreateRequest {
  /** 表示名 (必須 / 最大 100 文字) */
  label: string;
  /** MP3 バイナリの base64 (必須 / デコード後 5 MiB 以下) */
  content_base64: string;
  /** 音源長 (ミリ秒 / 任意) */
  duration_ms?: number;
}

/**
 * PUT /chimes/{chime_id} のリクエストボディ。
 * 指定した項目のみ更新される (省略した項目は現在値を維持する)。
 */
export interface ChimeUpdateRequest {
  /** 表示名 (省略時は変更しない) */
  label?: string;
  /** 音源を差し替える場合の MP3 base64 (省略時は現在の音源を維持) */
  content_base64?: string;
  /** 音源長 (ミリ秒 / 省略時は変更しない) */
  duration_ms?: number;
}

/** GET /chimes のレスポンス */
export interface ChimeListResponse {
  items: Chime[];
  count: number;
}

/** POST / PUT /chimes のレスポンス (チャイム + 履歴の登録結果) */
export type ChimeSaveResult = Chime & HistoryRecordResult;

/** DELETE /chimes/{chime_id} のレスポンス */
export interface ChimeDeleteResponse extends HistoryRecordResult {
  message: string;
  item: Chime;
}

/** GET /chimes/{chime_id}/audio のレスポンス (UI 試聴用の base64 MP3) */
export interface ChimeAudioResponse {
  content_type: string;
  content_base64: string;
}

// ---------------------------------------------------------------------------
// 定型文マスタ (PhraseTemplateMaster) — Issue #449 / SBI-05〜08
// ---------------------------------------------------------------------------
//
// 放送文面の「基本文」を言語別に保持するマスタ。受け入れ条件
// 「基本文に近い文章は定型文マスタを用いること」「定型マスターは OBA-Core で
// 編集できること」に対応する。
//
// ⚠️ `language_code` という名前だが、格納される値は Polly の `LanguageCode`
//    (`ja-JP` / `cmn-CN`) ではなく **language_key** (`ja` / `zh`) である。
//    放送地点の `languages` / `voice_settings` と同じキー体系
//    (`BroadcastLanguageKey`) で引けるようにするため。

/** 定型文マスタ 1 レコード (種別 × 言語で一意) */
export interface PhraseTemplate {
  /** 定型文の種別キー (例 "self_train_delay" / PhraseTemplateMaster の PK) */
  template_id: string;
  /**
   * 言語キー (language_key。例 "ja" / "en" / "zh" / "ko" / PhraseTemplateMaster の SK)。
   *
   * `BroadcastPoint.languages` と同じキー体系。型は `string` にしておき、
   * 妥当性は `isValidLanguageKey()` (`data/pollyVoices.ts`) で検証する
   * (言語をマスタへ追加してもフロントの型を変えずに済むようにするため)。
   */
  language_code: string;
  /** 管理画面での表示名 (例 "自列車遅延") */
  label: string | null;
  /** 定型文本体。`{route_name}` 等のプレースホルダを含む */
  body: string | null;
  /** 作成日時 (RFC 3339 UTC / `Z` 終端・秒精度) */
  created_at: string | null;
  /** 更新日時 (RFC 3339 UTC / `Z` 終端・秒精度) */
  updated_at: string | null;
  /**
   * この行が「DynamoDB 未登録 = コード内既定の文面」かどうか (Issue #527)。
   *
   * `GET /phrase-templates?include_defaults=true` のときのみ付与される。
   * `true` の行は実際にはマスタへ保存されていないが、**放送で実際に使われる文面**
   * なので、編集画面はこれを初期表示に使う (空欄で見せると「空欄で保存 → 意図せず
   * 既定に戻る」事故になる)。
   */
  is_default?: boolean;
}

/** POST /phrase-templates のリクエスト */
export interface PhraseTemplateCreateRequest {
  template_id: string;
  language_code: string;
  body: string;
  label?: string;
}

/** PUT /phrase-templates/{template_id}/{language_code} のリクエスト */
export interface PhraseTemplateUpdateRequest {
  body?: string;
  label?: string;
}

/** GET /phrase-templates のレスポンス */
export interface PhraseTemplateListResponse {
  items: PhraseTemplate[];
  count: number;
}

/** POST / PUT /phrase-templates のレスポンス (定型文 + 履歴の登録結果) */
export type PhraseTemplateSaveResult = PhraseTemplate & HistoryRecordResult;

/** DELETE /phrase-templates/{template_id}/{language_code} のレスポンス */
export interface PhraseTemplateDeleteResponse extends HistoryRecordResult {
  message: string;
  item: PhraseTemplate;
}

/** DELETE /phrase-templates (定型文マスタの初期化 / Issue #631) のレスポンス */
export interface PhraseTemplateResetResponse extends HistoryRecordResult {
  message: string;
  /** 削除したレコード数 */
  deleted_count: number;
}

export interface ApiErrorBody {
  message?: string;
}

// ---------------------------------------------------------------------------
// SSML 辞書マスタ (SsmlDictionaryMaster) — SBI-02
// ---------------------------------------------------------------------------
//
// 「変換元 → 置換後」の辞書を言語別に保守するマスタ。本 API は辞書の保守のみで、
// 放送文への適用は UpdateContents 側 (別 SBI) の責務。
//
// ⚠️ `language_code` に入るのは Polly の `LanguageCode` (`ja-JP`) ではなく **language_key** (`ja` / `en`)。

/**
 * 辞書の種別。将来の種別追加 (例: 数字の読み) に備え列挙で管理する。
 *
 * ⚠️ `lambda/control-panel-api/index.py` の `SSML_DICTIONARY_TYPES` と
 *    `docs/openapi/control-panel-api.yaml` の `SsmlDictionaryType` と同じ内容に保つこと。
 */
export const SSML_DICTIONARY_TYPES = ['reading', 'accent', 'text', 'ssml'] as const;

export type SsmlDictionaryType = (typeof SSML_DICTIONARY_TYPES)[number];

/** 保存は成功したが確認してほしい点 */
export interface SsmlDictionaryWarning {
  /** `replacement_in_other_source` | `accent_unverified` */
  code: string;
  message: string;
}

/** SSML 辞書 1 レコード (`(language_code, source_text)` で一意) */
export interface SsmlDictionaryEntry {
  /** 言語キー (language_key。SsmlDictionaryMaster の PK) */
  language_code: string;
  /** 変換元の文字列 (SsmlDictionaryMaster の SK) */
  source_text: string;
  dictionary_type: SsmlDictionaryType;
  /** 置換後 (reading: かな / accent: カタカナ + アクセント核 `'` / text: 自由なテキスト / ssml: SSML の断片) */
  replacement: string;
  /** 補足 / 出典 / 確認した実音声の所見 (未設定は null) */
  note: string | null;
  /** false の項目は放送文に適用されない */
  enabled: boolean;
  /** 作成日時 (RFC 3339 UTC / `Z` 終端・秒精度) */
  created_at: string | null;
  /** 更新日時 (RFC 3339 UTC / `Z` 終端・秒精度) */
  updated_at: string | null;
}

/** POST / PUT のレスポンス (辞書項目 + 保存時の警告) */
export interface SsmlDictionarySaveResult extends SsmlDictionaryEntry, HistoryRecordResult {
  warnings: SsmlDictionaryWarning[];
}

/** POST /ssml-dictionary のリクエスト */
export interface SsmlDictionaryCreateRequest {
  language_code: string;
  source_text: string;
  dictionary_type: SsmlDictionaryType;
  replacement: string;
  note?: string;
  enabled?: boolean;
}

/** PUT /ssml-dictionary/{language_code}/{source_text} のリクエスト */
export interface SsmlDictionaryUpdateRequest {
  dictionary_type?: SsmlDictionaryType;
  replacement?: string;
  /** 空文字を指定すると補足をクリアする */
  note?: string;
  enabled?: boolean;
}

/** GET /ssml-dictionary のレスポンス */
export interface SsmlDictionaryListResponse {
  items: SsmlDictionaryEntry[];
  count: number;
}

/** DELETE /ssml-dictionary/{language_code}/{source_text} のレスポンス */
export interface SsmlDictionaryDeleteResponse extends HistoryRecordResult {
  message: string;
  item: SsmlDictionaryEntry;
}

// ---------------------------------------------------------------------------
// 任意テキストテンプレートマスタ (FreeTextTemplateMaster)
// ---------------------------------------------------------------------------
//
// 放送地点マスタの任意テキスト放送「放送文面 (通常テキスト)」に流し込む定型の文面を
// 線区別に保持するマスタ。選択時に本文がコピーされるだけで、放送生成側は参照しない。
//
// ⚠️ `route_id` に `__all__` を入れると全線区共通。
//    `lambda/control-panel-api/index.py` の `FREE_TEXT_TEMPLATE_ALL_ROUTES` と同じ値に保つこと。

/** 保存は成功したが確認してほしい点 */
export interface FreeTextTemplateWarning {
  /** `duplicate_text` (同じ線区に同じ本文が登録済み) */
  code: string;
  message: string;
}

/**
 * 独自テンプレート 1 レコード (`(route_id, template_id)` で一意)。
 *
 * 配布テンプレート (`SyncedText`) も同じ形。
 */
export interface FreeTextTemplate {
  /** 線区 ID。`__all__` は全線区共通 */
  route_id: string;
  /** サーバが採番する UUID */
  template_id: string;
  /**
   * タイトル (最大 100 文字 / 管理用。放送には影響しない)。
   * タイトル機能の追加前に登録した既存データでは `null`。
   */
  title: string | null;
  /** 本文 (最大 3000 文字) */
  text: string;
  /** 備考 (最大 1000 文字 / 管理用。放送には影響しない)。未入力は `null` */
  note: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** POST / PUT のレスポンス (テンプレート + 保存時の警告) */
export interface FreeTextTemplateSaveResult extends FreeTextTemplate, HistoryRecordResult {
  warnings: FreeTextTemplateWarning[];
}

/** POST /free-text-templates のリクエスト */
export interface FreeTextTemplateCreateRequest {
  route_id: string;
  /** 必須 (空・空白のみは 400) */
  title: string;
  text: string;
  note?: string;
}

/** PUT /free-text-templates/{route_id}/{template_id} のリクエスト */
export interface FreeTextTemplateUpdateRequest {
  text: string;
  /** 省略すると既存の値を保持。既存のタイトルを空にすると 400 */
  title?: string;
  /** 省略すると既存の値を保持。空文字で削除 */
  note?: string;
}

/** GET /free-text-templates のレスポンス */
export interface FreeTextTemplateListResponse {
  items: FreeTextTemplate[];
  count: number;
}

/** DELETE /free-text-templates/{route_id}/{template_id} のレスポンス */
export interface FreeTextTemplateDeleteResponse extends HistoryRecordResult {
  message: string;
  item: FreeTextTemplate;
}

// ---------------------------------------------------------------------------
// 共有テキストマスタ (SyncedTextMaster)
// ---------------------------------------------------------------------------
//
// 任意テキスト放送が **参照** (コピーしない) する本文を線区別に保持するマスタ。
// 任意テキスト (FreeTextTemplateMaster) とは別テーブル・別機能で、編集 / 削除が参照先の放送に影響する。
// レスポンス形式は任意テキストと同じ (型を再利用する)。
//
// ⚠️ `route_id` に `__all__` を入れると全線区共通。
//    `lambda/control-panel-api/index.py` の `SYNCED_TEXT_ALL_ROUTES` と同じ値に保つこと。

/** 共有テキスト 1 レコード (`(route_id, template_id)` で一意) */
export type SyncedText = FreeTextTemplate;

/** 放送地点から共有テキストを参照するキー */
export interface SyncedTextRef {
  /** 線区 ID。`__all__` は全線区共通 */
  route_id: string;
  /** 共有テキストの UUID */
  template_id: string;
}

/** POST / PUT のレスポンス (共有テキスト + 保存時の警告) */
export type SyncedTextSaveResult = FreeTextTemplateSaveResult;

/** GET /synced-texts のレスポンス */
export type SyncedTextListResponse = FreeTextTemplateListResponse;

/** DELETE /synced-texts/{route_id}/{template_id} のレスポンス */
export type SyncedTextDeleteResponse = FreeTextTemplateDeleteResponse;

/** 削除拒否 (409) の参照元 1 件 (この共有テキストを参照している放送地点のコンテンツ) */
export interface SyncedTextReference {
  routeDirection: string | null;
  spotId: string | null;
  description: string | null;
  /** 放送地点の `contents` 内の位置 (0 始まり) */
  content_index: number;
}

/** DELETE が 409 (参照中) を返したときのボディ */
export interface SyncedTextInUseBody extends ApiErrorBody {
  reference_count: number;
  /** 参照元 (サーバは最大 50 件まで返す) */
  references: SyncedTextReference[];
}

/** 所属している権限グループの要約 (GET /me/permissions) */
export interface PermissionGroupSummary {
  group_id: string;
  name: string;
}

/**
 * GET /me/permissions のレスポンス (権限管理 SBI-4 / SBI-6)。
 *
 * `features` は所属全グループの機能キーの和集合。所属ゼロなら空配列。
 * 画面の出し分け専用で、API の認可は常にバックエンド (403) が行う。
 */
export interface MyPermissions {
  user_sub: string;
  permission_groups: PermissionGroupSummary[];
  features: string[];
}

// ---------------------------------------------------------------------------
// 権限管理の管理画面 (権限管理 SBI-8)
// ---------------------------------------------------------------------------

/** 機能カタログの区分 (`GET /features` の `scope`) */
export type PermissionFeatureScope = 'control_panel' | 'admin';

/** 権限を付与できる機能 1 件 (`GET /features`) */
export interface PermissionFeature {
  key: string;
  label: string;
  scope: PermissionFeatureScope | string;
}

/** GET /features のレスポンス */
export interface PermissionFeatureListResponse {
  items: PermissionFeature[];
  count: number;
}

/** 権限グループ 1 件 */
export interface PermissionGroup {
  group_id: string;
  name: string;
  description: string;
  /** 利用できる機能キー (カタログの定義順) */
  features: string[];
  created_at?: string | null;
  updated_at?: string | null;
}

/** GET /permission-groups のレスポンス */
export interface PermissionGroupListResponse {
  items: PermissionGroup[];
  count: number;
}

/** POST /permission-groups のリクエスト */
export interface PermissionGroupCreateRequest {
  name: string;
  description?: string;
  features?: string[];
}

/** PUT /permission-groups/{group_id} のリクエスト (指定した項目だけ更新される) */
export interface PermissionGroupUpdateRequest {
  name?: string;
  description?: string;
  features?: string[];
}

/** POST / PUT /permission-groups のレスポンス (権限グループ + 履歴の登録結果) */
export type PermissionGroupSaveResult = PermissionGroup & HistoryRecordResult;

/** DELETE /permission-groups/{group_id} のレスポンス */
export interface PermissionGroupDeleteResponse extends HistoryRecordResult {
  message: string;
  item: PermissionGroup;
}

/** ユーザー 1 件 (実体は Cognito の WebAppUserPool) */
export interface AdminUser {
  user_sub: string;
  email: string | null;
  /** Cognito のユーザー状態 (`CONFIRMED` / `FORCE_CHANGE_PASSWORD` など) */
  status: string | null;
  /** false は無効化済み (新しくサインインできない) */
  enabled: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  permission_groups: PermissionGroupSummary[];
}

/** GET /users のレスポンス */
export interface AdminUserListResponse {
  items: AdminUser[];
  count: number;
}

/** DELETE /users/{user_sub} のレスポンス */
export interface AdminUserDeleteResponse {
  message: string;
  item: AdminUser;
}

/** GET・PUT /users/{user_sub}/permission-groups のレスポンス */
export interface UserPermissions {
  user_sub: string;
  permission_groups: PermissionGroupSummary[];
  /** 所属全グループの機能キーの和集合 */
  features: string[];
}

// ---------------------------------------------------------------------------
// マスタ変更履歴 (MasterChangeHistory) — マスタ変更履歴 SBI 7
// docs/openapi/control-panel-api.yaml の `MasterHistory*` スキーマに対応する。
// ---------------------------------------------------------------------------

/** 履歴を記録するマスタ種別 */
export type MasterHistoryType =
  | 'broadcast_spot'
  | 'chime'
  | 'phrase_template'
  | 'ssml_dictionary'
  | 'free_text_template'
  | 'permission_group'
  | 'device';

/** 履歴の操作種別 */
export type MasterHistoryAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'REBOOT';

/** 1 項目の差分の種類 (追加 / 削除 / 変更) */
export type MasterHistoryChangeKind = 'added' | 'removed' | 'changed';

/**
 * 履歴一覧の 1 件 (① 変更一覧 / ② 対象データの履歴一覧)。
 * `before` / `after` / `changes` は含まない (詳細は `MasterHistoryDetail`)。
 */
export interface MasterHistoryListEntry {
  /** 対象データの識別子。② への遷移に使う (画面には表示しない) */
  entity_key: string;
  /** 履歴の版 (ULID。新しいほど大きい) */
  version: string;
  master_type: MasterHistoryType;
  /** 対象の表示名 (削除後も残る) */
  display_name?: string | null;
  action: MasterHistoryAction;
  /** 変更者 */
  actor?: string | null;
  /** 変更日時 (ISO 8601) */
  changed_at: string;
  /** 変更概要 (例: `半径変更、放送文変更 他1件`) */
  summary?: string | null;
}

/** GET /master-history のレスポンス (① / ②) */
export interface MasterHistoryListResponse {
  items: MasterHistoryListEntry[];
  count: number;
  /** ② のときだけ。実際に履歴を引いた `entity_key` */
  entity_key?: string;
  /** 続きがあるときだけ付く。不透明な値で、そのまま次のリクエストへ渡す */
  next_token?: string;
}

/** 1 項目の差分。除外ルールと日本語ラベルを通した後の値 */
export interface MasterHistoryChange {
  /** 項目の位置。画面には表示しない */
  path?: string;
  /** 日本語の項目名 */
  label?: string;
  kind?: MasterHistoryChangeKind;
  before?: unknown;
  after?: unknown;
}

/** GET /master-history/{version} のレスポンス (③ 履歴詳細) */
export interface MasterHistoryDetail extends MasterHistoryListEntry {
  changes?: MasterHistoryChange[];
  /** 変更前の完全なスナップショット (`CREATE` は null) */
  before?: Record<string, unknown> | null;
  /** 変更後の完全なスナップショット (`DELETE` は null) */
  after?: Record<string, unknown> | null;
  schema_version?: number | null;
}

/** POST /master-history/{version}/restore のレスポンス (履歴からの復元) */
export interface MasterHistoryRestoreResult {
  entity_key: string;
  /** 復元の元にした履歴の版 (復元自体は新しい版として履歴に残る) */
  restored_version: string;
  /** 削除済みの対象を作り直したとき true */
  created: boolean;
  /** 復元の履歴を記録できたか (false でも復元自体は成功している) */
  historyRecordSucceeded: boolean;
}

/** GET /master-history/diff のレスポンス (最新との比較) */
export interface MasterHistoryDiff {
  entity_key: string;
  from_version: string;
  /** 比較先 (最新) の版 */
  to_version: string;
  /** `from` が最新の版そのものか (true なら `changes` は空) */
  is_latest: boolean;
  /** 最新が DELETE か (true なら現在は削除されており、`changes` は空) */
  deleted: boolean;
  changes: MasterHistoryChange[];
}

/** ① 変更一覧の検索条件 (すべて任意) */
export interface MasterHistoryListParams {
  /** 省略で権限のある全種別 */
  masterType?: MasterHistoryType;
  action?: MasterHistoryAction;
  /** 変更発生日の開始 (JST の暦日 `YYYY-MM-DD`。その日を含む) */
  changedFrom?: string;
  /** 変更発生日の終了 (JST の暦日 `YYYY-MM-DD`。その日を含む) */
  changedTo?: string;
  /** 最終更新者 (大文字小文字を区別しない部分一致) */
  actor?: string;
  /**
   * 線区 (`route_id`。任意テキストの全線区共通は `__all__`)。
   * `masterType` が放送地点・任意テキストのときだけ指定できる (それ以外は 400)。
   */
  routeId?: string;
  /** ページサイズ (1〜100。省略でサーバ既定の 50) */
  limit?: number;
  /** 前回の応答の `next_token` */
  nextToken?: string;
}

/** ② 対象データの履歴一覧の検索条件 */
export interface MasterHistoryEntityListParams {
  /** ページサイズ (1〜100。省略でサーバ既定の 50) */
  limit?: number;
  /** 前回の応答の `next_token` */
  nextToken?: string;
}
